import { useContext, useEffect, useState } from 'react';
import batchTeacherStructureService from 'ApiService/Teacher/batch.teacher.service';
import { AuthContext } from 'contexts/AuthContext';
import toast from 'react-hot-toast';

const dayMap = {
  Monday: 'M', Tuesday: 'T', Wednesday: 'W', Thursday: 'Th',
  Friday: 'F', Saturday: 'S', Sunday: 'Su',
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const day = date.getDate().toString().padStart(2, '0');
  const month = date.toLocaleString('default', { month: 'short' });
  const year = date.getFullYear().toString().slice(-2);
  return `${day}${month}${year}`;
};

const formatSchedule = (daySchedules) => {
  if (!daySchedules || daySchedules.length === 0) return '';
  return daySchedules
    .map((s) => {
      const d = dayMap[s.day] || s.day;
      const t = s.startTime.replace(':00', '').replace(' ', '');
      return `${d}-${t}`;
    })
    .join(' | ');
};

const getBranchInitials = (branches) => {
  if (!branches || branches.length === 0) return '';
  return branches
    .map((br) => (br.name || '').split(',').pop().trim()[0] || '')
    .join('');
};

const getBatchDisplay = (batch) => {
  if (!batch) return '';
  const num = `B${batch.batchNumber}`;
  const date = formatDate(batch.startDate);
  const sched = formatSchedule(batch.daySchedules);
  const branch = getBranchInitials(batch.branches);
  let display = num;
  if (date) display += ` | ${date}`;
  if (sched) display += ` (${sched})`;
  if (branch) display += ` ${branch}`;
  return display;
};

const SelectBatch = ({
  setDisable,
  courses,
  setTestData,
  testData,
  loadingCourses,
}) => {
  const { state } = useContext(AuthContext);
  const [errors, setErrors] = useState([]);
  const [batchStructures, setBatchStructures] = useState([]);
  const [loadingStructures, setLoadingStructures] = useState(false);
  const [batches, setBatches] = useState([]);
  const [loadingBatches, setLoadingBatches] = useState(false);

  const handleCourseChange = (event) => {
    const courseId = event.target.value;
    const selectedCourse = courses.find(
      (course) => course._id === courseId
    );
    setTestData((prev) => ({
      ...prev,
      courseId: selectedCourse?._id || courseId,
      courseName: selectedCourse ? selectedCourse.courseName : '',
      assignedTo: [],
      structureId: '',
    }));
    setBatchStructures([]);
    setBatches([]);
  };

  const handleStructureChange = (event) => {
    const structureId = event.target.value;
    setTestData((prev) => ({ ...prev, structureId, assignedTo: [] }));
    setBatches([]);
  };

  const handleBatchToggle = (batchId) => {
    setTestData((prev) => ({
      ...prev,
      assignedTo: prev.assignedTo.includes(batchId)
        ? prev.assignedTo.filter((id) => id !== batchId)
        : [...prev.assignedTo, batchId],
    }));
  };

  useEffect(() => {
    if (!testData.courseId) {
      setBatchStructures([]);
      return;
    }
    (async () => {
      setLoadingStructures(true);
      try {
        const res = await batchTeacherStructureService.getBatchStructuresByCourse(testData.courseId);
        if (res.data.success) {
          setBatchStructures(res.data.batchStructures || []);
        } else {
          toast.error(res.data.message || 'Failed to load batch structures.');
        }
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to load batch structures.');
      } finally {
        setLoadingStructures(false);
      }
    })();
  }, [testData.courseId]);

  useEffect(() => {
    if (!testData.courseId || !testData.structureId) {
      setBatches([]);
      return;
    }
    (async () => {
      setLoadingBatches(true);
      try {
        const selectedBranch = state?.user?.branch?._id || state?.user?.branch?.name;
        const res = await batchTeacherStructureService.getBatchesByCourseStructure(
          testData.courseId,
          testData.structureId,
          selectedBranch
        );
        if (res.data.success) {
          setBatches(res.data.batches || []);
        } else {
          toast.error(res.data.message || 'Failed to load batches.');
        }
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to load batches.');
      } finally {
        setLoadingBatches(false);
      }
    })();
  }, [testData.courseId, testData.structureId, state?.user?.branch]);

  useEffect(() => {
    const newErrors = [];
    if (!testData?.courseId) {
      newErrors.push('Please select a course.');
    }
    if (!testData?.structureId) {
      newErrors.push('Please select a batch structure.');
    }
    if (!testData?.assignedTo?.length) {
      newErrors.push('Please select at least one batch.');
    }
    setErrors(newErrors);
    setDisable(newErrors.length > 0);
  }, [setDisable, testData]);

  return (
    <div className="w-full rounded-lg px-3 pt-7 md:px-8">
      <div className="flex justify-center">
        <h4 className="text-lg font-bold text-navy-700 dark:text-white">
          Select Class Information
        </h4>
      </div>

      <div className="mt-6 flex flex-col md:flex-row md:items-center md:justify-center md:gap-6">
        <div className="w-full md:w-1/2">
          <label className="ml-3 text-sm font-bold text-navy-700 dark:text-white">
            Select Course
          </label>
          <select
            onChange={handleCourseChange}
            value={testData?.courseId || ''}
            name="course"
            className="mt-2 w-full rounded-xl border border-gray-200 py-3 pl-2.5 pr-3 text-sm font-normal outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white"
          >
            <option value="">Click here to Select Course</option>
            {loadingCourses ? (
              <option>Loading courses...</option>
            ) : courses.length === 0 ? (
              <option
                className="cursor-pointer font-bold text-brand-500"
                value="create"
              >
                No courses available
              </option>
            ) : (
              courses.map((course, i) => (
                <option key={course._id} value={course._id}>
                  {i + 1}. {course.courseName}
                </option>
              ))
            )}
          </select>
        </div>

        <div className="w-full md:w-1/2">
          <label className="ml-3 text-sm font-bold text-navy-700 dark:text-white">
            Select Batch Structure
          </label>
          <select
            value={testData?.structureId || ''}
            onChange={handleStructureChange}
            disabled={!testData.courseId}
            className="mt-2 w-full rounded-xl border border-gray-200 py-3 pl-2.5 pr-3 text-sm font-normal outline-none dark:!border-white/10 dark:!bg-navy-800 dark:text-white disabled:opacity-50"
          >
            <option value="">Select Structure</option>
            {loadingStructures ? (
              <option>Loading...</option>
            ) : batchStructures.length === 0 ? (
              <option>No structures available</option>
            ) : (
              batchStructures.map((bs) => (
                <option key={bs._id} value={bs._id}>
                  {bs.name}
                </option>
              ))
            )}
          </select>
        </div>
      </div>

      <div className="mt-6">
        <label className="ml-3 text-sm font-bold text-navy-700 dark:text-white">
          Select Batches
        </label>
        <div className="mt-2 flex flex-wrap gap-2">
          {testData.assignedTo.map((batchId) => {
            const batch = batches.find((b) => b._id === batchId);
            return (
              <span
                key={batchId}
                className="inline-flex items-center rounded-md bg-blue-100 px-3 py-1 text-sm font-medium text-blue-800 dark:bg-blue-900 dark:text-blue-200"
              >
                {getBatchDisplay(batch) || batchId}
                <button
                  className="ml-2 text-blue-600 hover:text-blue-800 dark:text-blue-300 dark:hover:text-blue-100"
                  onClick={() => handleBatchToggle(batchId)}
                >
                  ×
                </button>
              </span>
            );
          })}
        </div>
        <div className="mt-2 grid max-h-48 grid-cols-1 gap-1 overflow-y-auto rounded-lg border border-gray-200 p-2 dark:border-gray-700 md:grid-cols-2">
          {loadingBatches ? (
            <p className="col-span-full py-2 text-center text-sm text-gray-500">Loading batches...</p>
          ) : batches.length === 0 ? (
            <p className="col-span-full py-2 text-center text-sm text-gray-500">
              {testData.structureId ? 'No batches available' : 'Select a structure first'}
            </p>
          ) : (
            batches.map((batch) => (
              <div
                key={batch._id}
                onClick={() => handleBatchToggle(batch._id)}
                className={`flex cursor-pointer items-center rounded-lg border px-3 py-2 transition-colors hover:bg-gray-50 dark:hover:bg-gray-700 ${
                  testData.assignedTo.includes(batch._id)
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/50'
                    : 'border-gray-200 dark:border-gray-700'
                }`}
              >
                <input
                  type="checkbox"
                  readOnly
                  checked={testData.assignedTo.includes(batch._id)}
                  className="mr-3 h-4 w-4 rounded border-gray-300"
                />
                <span className="whitespace-nowrap text-sm dark:text-white">
                  {getBatchDisplay(batch)}
                </span>
              </div>
            ))
          )}
        </div>
      </div>

      {errors.length > 0 && (
        <div className="mt-2 text-red-500">
          {errors.map((error, index) => (
            <p key={index}>{error}</p>
          ))}
        </div>
      )}
    </div>
  );
};

export default SelectBatch;